import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/**
 * M23 L23.04 — Document retention policies.
 *
 * Reads `GET /v1/document-retention` and lists the retention schedules with
 * their disposition action and enforcement flags.
 */
interface RetentionRow {
  readonly retentionId: string;
  readonly retentionCode: string;
  readonly retentionName: string;
  readonly retentionYears: number | null;
  readonly isPermanent: boolean;
  readonly dispositionAction: string;
  readonly autoEnforce: boolean;
  readonly recordStatusCode: string;
}

export function RetentionPoliciesPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<RetentionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: RetentionRow[] }>('/v1/document-retention', { token })
      .then((r) => { if (alive) setRows(r.data ?? []); })
      .catch((e) => { if (alive) { setError((e as Error).message); setRows([]); } });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="retention-policies">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('m23.retention.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('m23.retention.subtitle')}
      </p>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('m23.retention.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="retention-empty" style={{ color: 'var(--color-text-muted)' }}>{t('m23.retention.empty')}</p>
      ) : (
        <table aria-label="retention-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--color-border-default)', textAlign: 'left' }}>
              <th style={{ padding: '8px 4px' }}>{t('m23.retention.colCode')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m23.retention.colName')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m23.retention.colRetention')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m23.retention.colDisposition')}</th>
              <th style={{ padding: '8px 4px' }}>{t('m23.retention.colAutoEnforce')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.retentionId} data-testid={`retention-row-${p.retentionId}`} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                <td style={{ padding: '8px 4px', fontFamily: 'monospace' }}>{p.retentionCode}</td>
                <td style={{ padding: '8px 4px' }}>{p.retentionName}</td>
                <td style={{ padding: '8px 4px' }}>{p.isPermanent ? t('m23.retention.permanent') : t('m23.retention.years', { years: p.retentionYears ?? 0 })}</td>
                <td style={{ padding: '8px 4px' }}>{p.dispositionAction}</td>
                <td style={{ padding: '8px 4px' }}>{p.autoEnforce ? t('m23.retention.yes') : t('m23.retention.no')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
